import { z } from 'zod'
import { RideSource } from '../enums'
import { Id, Timestamp } from './common'

/** Distances in metres, times in seconds. Convert at the edges with units.ts. */
export const Ride = z.object({
  id: Id,
  bike_id: Id.nullable(),
  source: RideSource,
  external_id: z.string().nullable(),
  name: z.string().nullable(),
  started_at: Timestamp,
  distance_m: z.number(),
  moving_time_s: z.number().int(),
  elevation_gain_m: z.number().nullable(),
  created_at: Timestamp,
})
export type Ride = z.infer<typeof Ride>

export const RideList = z.object({ rides: z.array(Ride) })
export type RideList = z.infer<typeof RideList>

const MAX_DISTANCE_M = 2_000_000 // 2,000 km: longer than any single ride.
const MAX_TIME_S = 14 * 24 * 3600

export const CreateRideBody = z.object({
  bike_id: Id,
  started_at: Timestamp,
  distance_m: z.number().min(0).max(MAX_DISTANCE_M),
  moving_time_s: z.number().int().min(0).max(MAX_TIME_S),
  elevation_gain_m: z.number().min(0).max(100_000).nullable().optional(),
  name: z
    .string()
    .trim()
    .max(120)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional(),
})
export type CreateRideBody = z.input<typeof CreateRideBody>

/**
 * A ride imported from a GPX/FIT file or a phone's health store. The device
 * reads the file or workout and sends only this summary: never the track.
 * `external_id` (the file's SHA-256, or the workout's id) makes it idempotent.
 */
export const ImportRideBody = CreateRideBody.extend({
  bike_id: Id.nullable(),
  source: z.enum(['file', 'apple_health', 'health_connect']),
  external_id: z.string().trim().min(8).max(200),
  elapsed_time_s: z.number().int().min(0).max(MAX_TIME_S).optional(),
})
export type ImportRideBody = z.input<typeof ImportRideBody>

/** `created` is false when this file or workout was imported before. */
export const ImportRideResult = z.object({ ride: Ride, created: z.boolean() })
export type ImportRideResult = z.infer<typeof ImportRideResult>

/** `bike_id: null` moves a ride to the unassigned bucket. */
export const UpdateRideBody = CreateRideBody.extend({ bike_id: Id.nullable() })
  .partial()
  .refine((b) => Object.keys(b).length > 0, 'Nothing to update.')
export type UpdateRideBody = z.input<typeof UpdateRideBody>

export const RideListQuery = z.object({
  bike_id: Id.optional(),
  unassigned: z.enum(['true']).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
})

/** Derived usage: rides inside install windows plus adjustments. Never stored. */
export const Usage = z.object({
  distance_m: z.number(),
  moving_time_s: z.number(),
  rides: z.number().int(),
})
export type Usage = z.infer<typeof Usage>

export const UsageAdjustment = z.object({
  id: Id,
  distance_m: z.number(),
  moving_time_s: z.number().int(),
  effective_at: Timestamp,
  reason: z.string().nullable(),
  created_at: Timestamp,
})
export type UsageAdjustment = z.infer<typeof UsageAdjustment>

export const CreateAdjustmentBody = z
  .object({
    distance_m: z
      .number()
      .min(-MAX_DISTANCE_M * 50)
      .max(MAX_DISTANCE_M * 50)
      .default(0),
    moving_time_s: z
      .number()
      .int()
      .min(-MAX_TIME_S * 50)
      .max(MAX_TIME_S * 50)
      .default(0),
    reason: z
      .string()
      .trim()
      .max(200)
      .transform((v) => (v === '' ? null : v))
      .nullable()
      .optional(),
    effective_at: Timestamp.optional(),
  })
  .refine((b) => b.distance_m !== 0 || b.moving_time_s !== 0, 'Enter a distance or a time.')
export type CreateAdjustmentBody = z.input<typeof CreateAdjustmentBody>

export const AdjustmentParams = z.object({ id: Id, adjustmentId: Id })
